import React, { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useKeycloak } from '@react-keycloak/ssr';
import { useRouter } from 'next/router';
import type { EndpointResponse } from '@/types/endpoints';
import type { User } from '@/types/user';
import { asOrganizationId } from '@/session/identity';
import { useRouteOrganization } from '@/session/OrganizationIdentityContext';
import { acquireOperationContext, isSessionTransition, operationRequirement } from '@/session/operation';
import { scopedApi } from '@/session/scopedClients';
import { requestScopeFor } from '@/session/scopedTransport';

interface UserContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  refreshUser: () => Promise<void>;
}

export const UserContext = createContext<UserContextType>({
  user: null,
  loading: false,
  error: null,
  refreshUser: async () => {},
});

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { keycloak, initialized } = useKeycloak();
  const router = useRouter();
  const routeOrganization = useRouteOrganization();
  const requestSequenceRef = useRef(0);

  const fetchUser = useCallback(async (): Promise<void> => {
    const sequence = (requestSequenceRef.current += 1);
    if (!router.isReady || !routeOrganization) {
      setUser(null);
      setLoading(false);
      setError(router.isReady ? 'No organization context available' : null);
      return;
    }

    const organization = asOrganizationId(routeOrganization);
    const operation = acquireOperationContext(organization);
    if (!operation.isScopeCurrent()) return;
    setLoading(true);
    setError(null);

    try {
      const response = await scopedApi(requestScopeFor(organization)).get<EndpointResponse<User>>('userprofile/me', {
        operationRequirement: operationRequirement(operation, 'scope'),
      });
      if (sequence !== requestSequenceRef.current || !operation.isScopeCurrent()) return;
      if (response.data.isError) {
        setError('Failed to load user information');
        return;
      }
      setUser(response.data.result);
    } catch (caught) {
      if (sequence !== requestSequenceRef.current || !operation.isScopeCurrent() || isSessionTransition(caught)) return;
      setError('Failed to load user information');
    } finally {
      if (sequence === requestSequenceRef.current && operation.isScopeCurrent()) setLoading(false);
    }
  }, [routeOrganization, router.isReady]);

  useEffect(() => {
    if (initialized && keycloak?.authenticated && routeOrganization) {
      void fetchUser();
      return;
    }
    requestSequenceRef.current += 1;
    setUser(null);
    setLoading(false);
    setError(router.isReady && initialized && keycloak?.authenticated ? 'No organization context available' : null);
  }, [fetchUser, initialized, keycloak?.authenticated, routeOrganization, router.isReady]);

  return <UserContext.Provider value={{ user, loading, error, refreshUser: fetchUser }}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
